'use client'

import { domAnimation, LazyMotion, MotionConfig } from 'framer-motion'
import type { ReactNode } from 'react'

type SiteMotionProviderProps = {
  children: ReactNode
}

/*
  Two providers, deliberately nested.

  The inner one is synchronous, so `renderer` is defined on the very first
  render and every `m` component gets its visual element immediately. That
  matters: most sections start at `initial={{ opacity: 0 }}`, and an element
  with no visual element never runs the animation that reveals it. Loading
  the base features asynchronously would leave real content invisible until
  the chunk landed — a worse problem than the one we're solving.

  The outer one is asynchronous and exists only for its side effect. On mount
  it imports the layout-projection engine and hands it to framer-motion's
  `loadFeatures`, which writes into a module-level registry every `m`
  component reads from — so `layoutId` and `layout` start working site-wide
  once the chunk arrives, regardless of which provider is nearest. Its own
  `renderer` (undefined until loaded) is shadowed by the inner provider, so
  it can't blank anything out while it's in flight.

  `strict` makes a stray `motion` import throw in development, where the
  file-size regression would otherwise be invisible. It's a no-op in prod.
*/
const loadLayoutFeatures = () => import('@/lib/motion/layoutFeatures').then((mod) => mod.default)

export default function SiteMotionProvider({ children }: SiteMotionProviderProps) {
  return (
    <LazyMotion features={loadLayoutFeatures}>
      <LazyMotion features={domAnimation} strict>
        <MotionConfig
          reducedMotion="user"
          transition={{
            duration: 0.58,
            ease: [0.22, 1, 0.36, 1],
          }}
        >
          {children}
        </MotionConfig>
      </LazyMotion>
    </LazyMotion>
  )
}
